import { LiveAnnouncer } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty, coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatColumnDef, MatTable } from '@angular/material/table';
import { Code } from '@mtna/pojo-consumer-ts';
import { Code as MngCode } from '@mtna/pojo-management-ts';

import { CanTableEdit, CanTableEditCtor, mixinEditableTable } from '../core/common-behaviors';

/* eslint-disable @angular-eslint/no-inputs-metadata-property */
/* eslint-disable @angular-eslint/no-outputs-metadata-property */

// Boilerplate for applying mixins to RdsCodesTableComponent.
class RdsCodesTableBase {}
export const _RdsCodesTableMixinBase: CanTableEditCtor<Code | MngCode> = mixinEditableTable(RdsCodesTableBase);

/**
 * A table view of codes, can be editable.
 * This table assumes optimistic updates. When a Code's property is updated,
 * it will emit the udpated Code and make the change to the `codes` array.
 *
 * @author Will Davis <will.davis@mtna.us>
 */
@Component({
  selector: 'rds-c-codes-table',
  templateUrl: './codes-table.component.html',
  styleUrls: ['./codes-table.component.scss'],
  inputs: ['editable'],
  outputs: ['update'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RdsCodesTableComponent<C extends Code | MngCode = Code>
  extends _RdsCodesTableMixinBase
  implements CanTableEdit<Code | MngCode>, AfterContentInit
{
  static ngAcceptInputType_codes: Code[] | MngCode[] | null | undefined;
  static ngAcceptInputType_editable: BooleanInput;
  static ngAcceptInputType_length: NumberInput;
  static ngAcceptInputType_pageIndex: NumberInput;
  static ngAcceptInputType_pageSize: NumberInput;
  static ngAcceptInputType_showPaginator: BooleanInput;
  static ngAcceptInputType_sortColumnActive: string | null | undefined;
  static ngAcceptInputType_sortDirection: SortDirection | null | undefined;
  static ngAcceptInputType_sortDisabled: BooleanInput;

  @ViewChild(MatSort) sort?: MatSort;
  @ViewChild(MatTable, { static: true }) table!: MatTable<C>;
  @ContentChildren(MatColumnDef) columnDefs!: QueryList<MatColumnDef>;

  /** Codes to view or edit */
  @Input()
  get codes(): C[] {
    return this._codes;
  }
  set codes(codes: C[]) {
    this._codes = codes || [];
    this.checkAndUpdateFormArray();
  }
  private _codes: C[] = [];

  /** The columns to display, typically keyed on the Code properties. Defaults to `[codeValue, name]` */
  @Input() displayedColumns: Array<string | keyof C> = ['codeValue', 'name'];

  /** The length of the total number of codes that are being paginated */
  @Input()
  get length(): number {
    return this._length;
  }
  set length(value: number) {
    this._length = coerceNumberProperty(value);
  }
  private _length = 0;

  /** The zero-based page index of the displayed list of codes */
  @Input()
  get pageIndex(): number {
    return this._pageIndex;
  }
  set pageIndex(value: number) {
    this._pageIndex = coerceNumberProperty(value);
  }
  private _pageIndex = 0;

  /** Number of codes to display on a page */
  @Input()
  get pageSize(): number {
    return this._pageSize;
  }
  set pageSize(value: number) {
    this._pageSize = coerceNumberProperty(value);
  }
  private _pageSize = 50;

  /** Whether to use the internal mat-paginator. Defaults to true. */
  @Input()
  get showPaginator(): boolean {
    return this._showPaginator;
  }
  set showPaginator(show: boolean) {
    this._showPaginator = coerceBooleanProperty(show);
  }
  private _showPaginator = true;

  /** The column ID being actively sorted. */
  @Input()
  get sortColumnActive(): string {
    return this._sortColumnActive;
  }
  set sortColumnActive(active: string) {
    if (active !== this._sortColumnActive) {
      this._sortColumnActive = active || '';
    }
  }
  private _sortColumnActive = '';

  /** The direction to sort. */
  @Input()
  get sortDirection(): SortDirection {
    return this._sortDirection;
  }
  set sortDirection(d: SortDirection) {
    if (d !== this._sortDirection) {
      this._sortDirection = d || '';
    }
  }
  private _sortDirection: SortDirection = '';

  /** Whether to disable sorting. Defaults to false. */
  @Input()
  get sortDisabled(): boolean {
    return this._sortDisabled;
  }
  set sortDisabled(disabled: boolean) {
    this._sortDisabled = coerceBooleanProperty(disabled);
  }
  private _sortDisabled = false;

  /** Event emitted when the paginator changes the page size or page index */
  @Output() pageChange = new EventEmitter<PageEvent>();
  /** Event emitted when table sorting has been changed. */
  @Output() sortChange = new EventEmitter<Sort>();

  _TEXT_name = $localize`Name`;

  constructor(private _fb: FormBuilder, private _liveAnnouncer: LiveAnnouncer) {
    super();
  }

  checkAndUpdateFormArray() {
    if (this.editable && this.codes.length) {
      this._formArray = this._fb.array(
        this.codes.map((code) =>
          this._fb.group({
            name: this._fb.control(code.name),
            codeValue: this._fb.control(code.codeValue, Validators.required),
          })
        )
      );
    }
  }

  ngAfterContentInit() {
    // Update the column defs in case custom columns are injected via ng-content.
    this.columnDefs.forEach((columnDef) => this.table.addColumnDef(columnDef));
  }

  updateResource(previousCode: C, updatedCode: C) {
    this.codes = this.codes.map((c) => (c.codeValue === previousCode.codeValue ? updatedCode : c));
  }

  /** Emits the sort change & announces to a screen reader that sorting has occured/been cleared */
  _sortChange(sort: Sort) {
    this.sortChange.emit(sort);
    if (sort.direction && sort.active) {
      sort.direction === 'asc'
        ? this._liveAnnouncer.announce($localize`:Notification|informs the user about table sorting:Sorting codes ascending`)
        : this._liveAnnouncer.announce($localize`:Notification|informs the user about table sorting:Sorting codes descending`);
    } else {
      this._liveAnnouncer.announce($localize`:Notification|informs the user sorting is removed:Sorting cleared`);
    }
  }

  _trackByCode(index: number, code: C | undefined): string | number {
    if (!code) {
      return index;
    }
    return code.codeValue;
  }
}
